import { useEffect, useState } from "react";
import { useUserContext, useAdminContext } from "../../contexts";

//components
import { Reachback } from "./SampleResults/Reachback";
import { PlotAccordian } from "./SampleResults/PlotAccordian";
import SampleCons from "../../constants/sample";
import ResultCard from "./SampleResults/ResultCard";

//api calls
import { useReferenceSpectrum } from "../../api";

export default function SampleResults({ sample }) {
  const [presumed, setPresumed] = useState();

  const { user, settings } = useUserContext();
  const { selectedCustomer, customers } = useAdminContext();

  let sampleCountry =
    user.role === "admin"
      ? customers
          .find((customer) => customer.customerId === sample.customerId)
          ?.locations.find((loc) => loc.locationId === sample?.locationId)
          ?.country
      : user?.locID?.find((loc) => loc.locationId === sample?.locationId)
          ?.country;

  useEffect(() => {
    const isPrescription = SampleCons.NEW_PRESUMED_SUBSTANCES.find(
      (type) => type.label === "Prescription Medications",
    ).drugs.includes(sample.presumedSubstance);

    if (isPrescription || sample.presumedSubstance === "Other") {
      setPresumed(null);
    } else if (
      sample.presumedSubstance === "Fentalog (Fentanyl, Fluorfentanyl, etc.)"
    ) {
      setPresumed("Fentalog [Fentanyl]");
    } else if (
      sample.presumedSubstance ===
      "Benzodiazepines (Bromazalom, Flubromazolam, Clonazepam, etc.)"
    ) {
      setPresumed("Benzodiazepine [Bromazolam]");
    } else if (sample.presumedSubstance === "Nitazene") {
      setPresumed("Nitazene [Metonitazene]");
    } else if (sample.presumedSubstance === "Dextromethorphan (DXM)") {
      setPresumed("Dextromethorphan");
    } else if (sample.presumedSubstance === "Phencyclidine (PCP)") {
      setPresumed("PCP");
    } else if (sample.presumedSubstance === "MDMA/MDA") {
      setPresumed("MDMA");
    } else setPresumed(sample.presumedSubstance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter tests for bulk and trace
  let bulkTests = sample.tests.filter((test) => test.scanType === "bulk-burst");
  let traceTests = sample.tests.filter(
    (test) => test.scanType === "trace-burst",
  );

  // Get all findings from bulk and trace tests. Use reachback findings if available
  let bulkFindings = bulkTests
    .map((test) => {
      if (test.rb_findings && test.rb_findings.length > 0) {
        return test.rb_findings;
      } else {
        return test.findings;
      }
    })
    .flat();

  let traceFindings = traceTests
    .map((test) => {
      if (test.rb_findings && test.rb_findings.length > 0) {
        return test.rb_findings;
      } else {
        return test.findings;
      }
    })
    .flat();

  //fetch the bulk reference spectra
  const { results: bulkRefData, isSuccess: isBulkRefSuccess = false } =
    useReferenceSpectrum(
      bulkFindings.map((finding) => {
        return {
          collection: "bulk-burst",
          // TODO: remove legacy `substance` fallback once old sample format is retired
          analyte: finding.substanceModel ?? finding.substance,
        };
      }),
    );

  //fetch the trace reference spectra
  const { results: traceRefData, isSuccess: isTraceRefSuccess = false } =
    useReferenceSpectrum(
      traceFindings.map((finding) => {
        return {
          collection: "trace-burst",
          // TODO: remove legacy `substance` fallback once old sample format is retired
          analyte: finding.substanceModel ?? finding.substance,
        };
      }),
    );

  //fetch presumed substance reference spectra - bulk
  const {
    results: presumedBulkRefData,
    isSuccess: isPresumedBulkRefSuccess = false,
  } = useReferenceSpectrum([
    {
      collection: "bulk-burst",
      analyte: presumed,
    },
  ]);
  const {
    results: presumedTraceRefData,
    isSuccess: isPresumedTraceRefSuccess = false,
  } = useReferenceSpectrum([
    {
      collection: "trace-burst",
      analyte: presumed,
    },
  ]);

  return (
    <div className='my-2 full-height'>
      <div className='card full-height'>
        <div className='card-body'>
          <h5 className='card-title text-primary'>
            Amplifi ID
            <sup>
              <small>TM</small>
            </sup>{" "}
            Sample Results
          </h5>

          <div className='d-flex flex-wrap justify-content-between '>
            <div className='col-12 col-xl-6 mb-1 order-1'>
              <h6>
                Bulk Scan{" "}
                {"bulkErrorCheck" in (sample.reachback?.reason ?? {}) &&
                  sample.reachback?.status === "inProgress" && (
                    <i
                      // className='bi bi-exclamation-triangle-fill'
                      className='bi-hourglass-split'
                      style={{
                        color: "#AE0000",
                        marginRight: "10px",
                      }}
                    ></i>
                  )}
              </h6>
              <div className='d-flex flex-wrap justify-content-left gap-2'>
                {bulkTests.length > 0 ? (
                  bulkFindings.map((finding) => (
                    <ResultCard
                      // TODO: remove legacy `substance` fallback once old sample format is retired
                      result={finding.substanceCustomer ?? finding.substance}
                      key={finding.substanceModel ?? finding.substance}
                      country={sampleCountry}
                    />
                  ))
                ) : (
                  <p>No bulk tests found</p>
                )}
              </div>
            </div>

            <div className='col-12 col-xl-6 mb-1 order-3 order-xl-2'>
              <h6>
                Trace Scan{" "}
                {"traceErrorCheck" in (sample.reachback?.reason ?? {}) &&
                  sample.reachback?.status === "inProgress" && (
                    <i
                      // className='bi bi-exclamation-triangle-fill'
                      className='bi-hourglass-split'
                      style={{
                        color: "#AE0000",
                        marginRight: "10px",
                      }}
                    ></i>
                  )}
              </h6>
              <div className='d-flex flex-wrap justify-content-left gap-2'>
                {traceTests.length > 0 ? (
                  traceFindings.map((finding) => (
                    <ResultCard
                      // TODO: remove legacy `substance` fallback once old sample format is retired
                      result={finding.substanceCustomer ?? finding.substance}
                      key={finding.substanceModel ?? finding.substance}
                      country={sampleCountry}
                    />
                  ))
                ) : (
                  <p>No trace tests found</p>
                )}
              </div>
            </div>
            <div className='col-12 col-xl-6 mb-2 order-2 order-xl-3'>
              {bulkTests.length > 0 && (
                <PlotAccordian
                  refData={bulkRefData}
                  presumedRefData={presumedBulkRefData}
                  isRefSuccess={isBulkRefSuccess && isPresumedBulkRefSuccess}
                  header='View Bulk Spectra'
                  processedData={bulkTests.map(
                    (test) => test.processed_data_plot,
                  )}
                  wavenumbers={bulkTests.map((test) => test.wavenumber_plot)}
                />
              )}
            </div>
            <div className='col-12 col-xl-6 mb-2 order-4'>
              {traceTests.length > 0 && (
                <PlotAccordian
                  refData={traceRefData}
                  presumedRefData={presumedTraceRefData}
                  isRefSuccess={isTraceRefSuccess && isPresumedTraceRefSuccess}
                  header='View Trace Spectra'
                  processedData={traceTests.map(
                    (test) => test.processed_data_plot,
                  )}
                  wavenumbers={traceTests.map((test) => test.wavenumber_plot)}
                />
              )}
            </div>
          </div>
          <Reachback sample={sample} />
        </div>
      </div>
    </div>
  );
}
